import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {BreadcrumbService} from 'xng-breadcrumb';
import {HttpClient} from '@angular/common/http';
import {from, switchMap} from 'rxjs';
import {environment} from '../../environments/environment';
import {TokenStorageService} from '../_services/token-storage.service';
import {OrderService, ShippingAddressPayload} from '../shared/order.service';
import {dropshipShipsInLabel, isDropshipListing} from '../shared/fulfillment.utils';
import {ListingAttributeValueView} from '../shared/attribute-schema.service';

@Component({
  standalone: false,
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.css']
})
export class CheckoutComponent implements OnInit {
  itemId = '';
  itemTitle = '';
  itemPrice: number | null = null;
  fulfillmentLabel = '';
  shipsLabel: string | null = null;
  loading = true;
  submitting = false;
  error = '';

  shipping: ShippingAddressPayload = {
    fullName: '',
    line1: '',
    line2: '',
    city: '',
    region: '',
    postalCode: '',
    country: 'IE',
    phone: '',
    saveAsDefault: true
  };

  private attributes: ListingAttributeValueView[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private orders: OrderService,
    private tokens: TokenStorageService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.set('@Checkout', 'Checkout');
    if (!this.tokens.getToken()) {
      this.router.navigate(['/login'], {queryParams: {returnUrl: this.router.url}});
      return;
    }
    this.itemId = this.route.snapshot.queryParamMap.get('itemId') || '';
    if (!this.itemId) {
      this.error = 'Missing listing.';
      this.loading = false;
      return;
    }
    this.loadItem();
  }

  get needsShipping(): boolean {
    return !this.attributes.length
      || !this.attributes.some(a => a.code === 'fulfillment' && `${a.value || ''}`.toLowerCase() === 'local_pickup');
  }

  submit(): void {
    this.error = '';
    if (this.needsShipping) {
      if (!this.shipping.fullName?.trim() || !this.shipping.line1?.trim()
          || !this.shipping.city?.trim() || !this.shipping.postalCode?.trim()
          || !this.shipping.country?.trim()) {
        this.error = 'Please complete the shipping address.';
        return;
      }
    }
    this.submitting = true;
    const payload = this.needsShipping ? this.shipping : null;
    this.orders.createOrder(this.itemId, payload).subscribe({
      next: order => {
        this.submitting = false;
        this.router.navigate(['/payment'], {queryParams: {orderId: order.orderId}});
      },
      error: err => {
        this.submitting = false;
        this.error = err?.error?.error || 'Could not create order.';
      }
    });
  }

  private loadItem(): void {
    from(environment).pipe(
      switchMap((env: any) =>
        this.http.get<any>(`${env.backendURL}/item`, {params: {id: this.itemId}})
      )
    ).subscribe({
      next: (res) => {
        const item = res?.item || res;
        this.itemTitle = item?.name || 'Listing';
        this.itemPrice = item?.value != null ? Number(item.value) : null;
        this.attributes = res?.attributes || [];
        if (isDropshipListing(this.attributes)) {
          this.fulfillmentLabel = 'Dropship';
          this.shipsLabel = dropshipShipsInLabel(this.attributes);
        } else {
          const f = this.attributes.find(a => a.code === 'fulfillment');
          this.fulfillmentLabel = (f?.displayValue || f?.value?.toString() || 'Standard') as string;
        }
        this.loading = false;
      },
      error: () => {
        this.error = 'Listing not found.';
        this.loading = false;
      }
    });
  }
}
